import React, { FC, useRef, useState } from 'react';
import {
  Grid,
  makeStyles,
  Theme,
  IconButton,
  Typography,
  TableContainer,
  TextField,
  Table,
  CircularProgress,
  TableRow,
  TableCell,
  TableBody,
  Hidden,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Button,
  Fade
} from '@material-ui/core';
import clx from 'clsx';
import useRole from 'hooks/useRole';
import ListProduct from '../ListProduct';
import Skeleton from '@material-ui/lab/Skeleton';
import NumberFormat from 'components/NumberFormat';
import { BLACK, WHITE, GREEN } from 'constants/colors';
import EditIcon from '@material-ui/icons/Edit';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckIcon from '@material-ui/icons/Check';
import NumberFormatMask from 'components/NumberFormatMask';
import TimerOffTwoToneIcon from '@material-ui/icons/TimerOffTwoTone';
import CCATMP from 'images/NewLogo.jpg';
import useRouter from 'hooks/useRouter';
import { format } from 'date-fns';
import ModalPermission from './ModalPermission';

interface Props {
  invoice: InvoiceModel;
  company: CompanyModel;
  isLoadingData: boolean;
  invoiceItem: InvoiceItemModel[];
  invoicePayment?: InvoicePaymentModel[];
  openTotalPrice: boolean;
  loadingTotalPrice: boolean;
  isFinance: boolean;
  isInvoice: boolean;
  totalPrice: number;
  isRequestEdit: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  item: { id: number; notes: string; isRequestEdit: boolean }[];
  indexCollapse: number;
  indexEdit: number;
  type: string;
  loadingEdit: boolean;
  loadingRequest: boolean;
  isCancel: boolean;
  handleOpenRemark: () => void;
  onDeleteItem: (id: number) => void;
  onRefresh: () => void;
  onUpdate: () => void;
  individualEdit: (item: InvoiceItemModel) => void;
  handleRequest: () => void;
  handleEdit: (index: number, type: string) => void;
  handleOpenCollapse: (index: number) => React.MouseEventHandler;
  handleRequestNote: (item: { id: number; notes: string }) => void;
  handleRequestCheck: (item: { id: number; notes: string; isRequestEdit: boolean }) => void;
  handleRequestEdit: () => void;
  handleOpenUpdateTotalPrice: () => void;
  handleCancelUpdateTotalPrice: () => void;
  updateTotalPrice: () => void;
  setTotalPrice: React.Dispatch<React.SetStateAction<number>>;
  setOpenPartner: React.Dispatch<React.SetStateAction<boolean>>;
}

const useStyles = makeStyles((theme: Theme) => ({
  cellSummaryRight: {
    border: 'none',
    padding: 4,
    fontSize: '15px',
    lineHeight: '22px',
    fontFamily: 'Rubik',
    fontWeight: 500,
    fontStyle: 'normal',
    color: BLACK
  },
  cellSummaryLeft: {
    width: '35%',
    border: 'none',
    padding: '4px 4px 4px 4px',
    fontSize: '15px',
    lineHeight: '22px',
    fontFamily: 'Rubik',
    fontWeight: 400,
    fontStyle: 'normal',
    color: BLACK
  },
  cellInfoRight: {
    padding: '2px 0px 2px 2px',
    border: 'none',
    fontSize: '15px',
    lineHeight: '22px',
    fontFamily: 'Rubik',
    fontWeight: 400,
    fontStyle: 'normal'
  },
  cellInfoLeft: {
    padding: '2px 0px 2px 0px',
    border: 'none',
    fontSize: '15px',
    lineHeight: '22px',
    fontFamily: 'Rubik',
    fontWeight: 400,
    fontStyle: 'normal'
  },
  cellSummaryRightLink: {
    border: 'none',
    padding: 4,
    fontSize: '15px',
    lineHeight: '22px',
    fontFamily: 'Rubik',
    fontWeight: 500,
    fontStyle: 'normal',
    color: BLACK,
    textDecorationLine: 'underline',
    cursor: 'pointer'
  },
  note: {
    overflowX: 'auto'
  },
  cellOrder: {
    marginBottom: 1,
    padding: '2px 0px 2px 2px',
    border: 'none'
  },
  cellOrderRight: {
    width: '35%',
    fontSize: '15px',
    lineHeight: '22px',
    fontFamily: 'Rubik',
    fontWeight: 500,
    fontStyle: 'normal',
    color: BLACK
  },
  cellOrderRightUnderline: {
    padding: '2px 0px 2px 2px',
    border: 'none',
    fontSize: '15px',
    lineHeight: '22px',
    fontFamily: 'Rubik',
    fontWeight: 400,
    fontStyle: 'normal',
    textDecorationLine: 'underline',
    cursor: 'pointer'
  },
  cellOrderLeft: {
    width: '30%'
  },
  titleOrder: {
    color: BLACK,
    marginBottom: '16px'
  },
  textTooltip: {
    color: WHITE
  },
  timeOut: {
    animation: 'shake 0.5s',
    animationIterationCount: 'infinite'
  },
  refresh: {
    backgroundColor: GREEN,
    color: WHITE,
    '&:hover': {
      backgroundColor: GREEN
    }
  },
  '@keyframes shake': {
    '0%': { transform: 'translate(1px, 1px) rotate(0deg)' },
    '10%': { transform: ' translate(-1px, -2px) rotate(-1deg)' },
    '20%': { transform: ' translate(-3px, 0px) rotate(1deg)' },
    '30%': { transform: ' translate(3px, 2px) rotate(0deg)' },
    '40%': { transform: ' translate(1px, -1px) rotate(1deg)' },
    '50%': { transform: ' translate(-1px, 2px) rotate(-1deg)' },
    '60%': { transform: ' translate(-3px, 1px) rotate(0deg)' },
    '70%': { transform: ' translate(3px, 1px) rotate(-1deg)' },
    '80%': { transform: ' translate(-1px, -1px) rotate(1deg)' },
    '90%': { transform: ' translate(1px, 2px) rotate(0deg)' },
    '100%': { transform: ' translate(1px, -2px) rotate(-1deg)' }
  }
}));

const ContentInvoice: FC<Props> = props => {
  const { history } = useRouter();
  const classes = useStyles();
  const {
    invoice,
    isLoadingData,
    company,
    invoiceItem,
    invoicePayment,
    openTotalPrice,
    totalPrice,
    isFinance,
    loadingTotalPrice,
    isRequestEdit,
    item,
    indexCollapse,
    indexEdit,
    type,
    isCancel,
    loadingEdit,
    loadingRequest,
    isAdmin,
    isSuperAdmin,
    onDeleteItem,
    onRefresh,
    handleEdit,
    isInvoice,
    onUpdate,
    handleRequest,
    individualEdit,
    handleOpenCollapse,
    handleRequestNote,
    handleRequestCheck,
    handleRequestEdit,
    setTotalPrice,
    handleOpenUpdateTotalPrice,
    handleCancelUpdateTotalPrice,
    updateTotalPrice,
    setOpenPartner
  } = props;
  const [open, setOpen] = useState<boolean>(false);
  const realOriginalPrice = totalPrice + invoice.totalDiscount + invoice.totalPiece;

  const handleLink = (id: Number) => {
    if (isFinance || isInvoice) {
      setOpen(true);
    } else {
      history.push(`/penjualan/${id}`);
    }
  };
  const handleLinkSJ = (id: Number) => {
    history.push(`/surat-jalan/${id}`);
  };

  return (
    <Grid container direction='row' spacing={1}>
      <Hidden smDown xsDown>
        <Grid item lg={8} sm={8} md={8} xs={8}>
          <img src={CCATMP} alt='company' width='236px' height='236' />
        </Grid>
      </Hidden>

      <Grid container item lg={4} sm={12} md={4} xs={12} justify='flex-end'>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell align='right' colSpan={2} className={classes.cellOrder}>
                  <Typography variant='h1' className={classes.titleOrder}>
                    {invoice.isDue && (
                      <span style={{ verticalAlign: 'middle' }}>
                        <Tooltip
                          title={
                            <Typography variant='subtitle2' className={classes.textTooltip}>
                              Invoice ini sudah melewati tanggal pelunasan.
                            </Typography>
                          }
                        >
                          <TimerOffTwoToneIcon color='error' className={classes.timeOut} />
                        </Tooltip>
                      </span>
                    )}
                    &nbsp; Invoice Penjualan
                  </Typography>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell align='left' className={clx(classes.cellOrder, classes.cellOrderLeft)}>
                  <Typography variant='subtitle2'>ID Penjualan</Typography>
                </TableCell>
                <TableCell align='right' className={clx(classes.cellOrder, classes.cellOrderRight)}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : invoice.invoiceNumber}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell align='left' className={clx(classes.cellOrder, classes.cellOrderLeft)}>
                  <Typography variant='subtitle2'>Ref So</Typography>
                </TableCell>
                <TableCell
                  align='right'
                  className={clx(classes.cellOrder, classes.cellOrderRightUnderline)}
                  onClick={() => handleLink(invoice.SalesOrderId || 0)}
                >
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : invoice.orderNumber || '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='left' className={clx(classes.cellOrder, classes.cellOrderLeft)}>
                  <Typography variant='subtitle2'>Ref SJ</Typography>
                </TableCell>
                <TableCell
                  align='right'
                  className={clx(classes.cellOrder, classes.cellOrderRightUnderline)}
                  onClick={() => handleLinkSJ(invoice.deliveryId || 0)}
                >
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : invoice.deliveryNumber || '-'}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell align='left' className={clx(classes.cellOrder, classes.cellOrderLeft)}>
                  <Typography variant='subtitle2'>Tanggal Order</Typography>
                </TableCell>
                <TableCell align='right' className={clx(classes.cellOrder, classes.cellOrderRight)}>
                  {isLoadingData ? (
                    <Skeleton variant='text' width='100%' />
                  ) : invoice.orderDate ? (
                    format(new Date(invoice.orderDate), 'dd-MM-yyyy')
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='left' className={clx(classes.cellOrder, classes.cellOrderLeft)}>
                  <Typography variant='subtitle2'>Rute</Typography>
                </TableCell>
                <TableCell align='right' className={clx(classes.cellOrder, classes.cellOrderRight)}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : invoice.zoneName || '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='left' className={clx(classes.cellOrder, classes.cellOrderLeft)}>
                  <Typography variant='subtitle2'>Jatuh Tempo</Typography>
                </TableCell>
                <TableCell align='right' className={clx(classes.cellOrder, classes.cellOrderRight)}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : format(new Date(invoice.dueDate), 'dd-MM-yyyy')}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='left' className={clx(classes.cellOrder, classes.cellOrderLeft)}>
                  <Typography variant='subtitle2'>Batas Pelunasan</Typography>
                </TableCell>
                <TableCell align='right' className={clx(classes.cellOrder, classes.cellOrderRight)}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : format(new Date(invoice.paymentDue || 0), 'dd-MM-yyyy')}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='left' className={clx(classes.cellOrder, classes.cellOrderLeft)}>
                  <Typography variant='subtitle2'>Sales</Typography>
                </TableCell>
                <TableCell align='right' className={clx(classes.cellOrder, classes.cellOrderRight)}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : invoice.salesName || '-'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item lg={6} sm={12} md={6} xs={12}>
        <Typography variant='h5' align='left'>
          Nama Perusahaan
        </Typography>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell align='left' style={{ fontWeight: 500, color: BLACK }} className={classes.cellInfoLeft}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : company.name || '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='left' className={classes.cellInfoLeft}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : company.address || '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='left' className={classes.cellInfoLeft}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : 'Telp: ' + company.phoneNumber || '-'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='left' className={classes.cellInfoLeft}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : 'No Hp: ' + (company.cellPhoneNumber || '-')}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='left' className={classes.cellInfoLeft}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : 'Email: ' + (company.email || '-')}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item lg={6} md={6} sm={12} xs={12}>
        <Typography variant='h5' align='right'>
          Order Penjualan dari
        </Typography>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell onClick={() => setOpenPartner(true)} align='right' className={classes.cellOrderRightUnderline}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : invoice.Partner ? invoice.Partner.name : '-'}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell align='right' className={classes.cellInfoRight}>
                  {isLoadingData ? (
                    <Skeleton variant='text' width='100%' />
                  ) : invoice.Partner ? (
                    <Tooltip
                      interactive
                      arrow
                      open={!invoice.Partner.remarkAddress || invoice.Partner.remarkAddress === ''}
                      title={
                        <>
                          <div style={{ textAlign: 'center' }}>
                            <Typography gutterBottom>Remark Alamat belum diisi</Typography>
                            <Button size='small' onClick={props.handleOpenRemark}>
                              Klik disini
                            </Button>
                          </div>
                        </>
                      }
                    >
                      <span
                        style={{ textDecoration: !invoice.Partner.remarkAddress || invoice.Partner.remarkAddress === '' ? 'underline' : 'none' }}
                      >{`${invoice.Partner.address}, (${invoice.Partner.remarkAddress || '-'})`}</span>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right' className={classes.cellInfoRight}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : `Telp ${(invoice.Partner && invoice.Partner.phoneNumber) || '-'}`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right' className={classes.cellInfoRight}>
                  {isLoadingData ? (
                    <Skeleton variant='text' width='100%' />
                  ) : (
                    'No Hp: ' + ((invoice.Partner && invoice.Partner.cellPhoneNumber) || '-')
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right' className={classes.cellInfoRight}>
                  {isLoadingData ? <Skeleton variant='text' width='100%' /> : 'Email: ' + ((invoice.Partner && invoice.Partner.email) || '-')}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      <Grid item lg={6} sm={6} md={6} xs={6}>
        <Typography variant='body2'>Total Item : {invoiceItem.length}</Typography>
      </Grid>

      <Grid container justify='flex-end' item lg={6} sm={6} md={6} xs={6}>
        {isAdmin && (
          <FormControlLabel
            value='start'
            control={<Checkbox checked={isRequestEdit} onChange={handleRequestEdit} color='primary' />}
            label='Kirim Permintaan Edit'
            labelPlacement='start'
            disabled={isCancel}
          />
        )}
      </Grid>

      <Grid container item lg={12} sm={12} md={12} xs={12}>
        <ListProduct
          isCanceledInvoice={isCancel}
          isRequestEdit={isRequestEdit}
          isLoadingData={isLoadingData}
          invoiceItem={invoiceItem}
          item={item}
          indexCollapse={indexCollapse}
          indexEdit={indexEdit}
          rute={invoice.zoneName || ''}
          type={type}
          loadingEdit={loadingEdit}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          onDeleteItem={onDeleteItem}
          onUpdate={onUpdate}
          individualEdit={individualEdit}
          handleEdit={handleEdit}
          handleOpenCollapse={handleOpenCollapse}
          handleRequestNote={handleRequestNote}
          handleRequestCheck={handleRequestCheck}
        />
      </Grid>

      <Grid item lg={6} sm={6} md={6} xs={6}>
        <Fade in={isRequestEdit}>
          <Button size='small' onClick={handleRequest}>
            Simpan Perubahan dari ({item.length} Item) {loadingRequest && <CircularProgress color='inherit' size='small' />}
          </Button>
        </Fade>
      </Grid>

      <Grid container justify='flex-end' item lg={6} sm={6} md={6} xs={6}>
        {!isSuperAdmin && (
          <Tooltip title='Memuat Ulang'>
            <Button size='small' color='inherit' onClick={onRefresh} className={classes.refresh}>
              Memuat Ulang
            </Button>
          </Tooltip>
        )}
      </Grid>

      <Grid container direction='row' item xl={6} lg={6} sm={12} md={6} xs={12}>
        <Grid item lg={12} sm={12} md={12} xs={12}>
          <Typography variant='h5'>Keterangan</Typography>
        </Grid>

        <Grid item lg={12} sm={12} md={12} xs={12}>
          <div className={classes.note} dangerouslySetInnerHTML={{ __html: invoice.notes || '-' }}></div>
        </Grid>
      </Grid>

      <Grid container item lg={12} sm={12} md={12} xs={12}>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell align='right' className={classes.cellSummaryRight}>
                  Subtotal
                </TableCell>
                <TableCell align='right' className={classes.cellSummaryLeft}>
                  <NumberFormat value={realOriginalPrice} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right' className={classes.cellSummaryRight}>
                  Diskon
                </TableCell>
                <TableCell align='right' className={classes.cellSummaryLeft}>
                  <NumberFormat value={invoice.totalDiscount} prefix={'(Rp'} suffix={')'} thousandSeparator={true} displayType='text' />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right' className={classes.cellSummaryRight}>
                  Total Harga
                </TableCell>
                <TableCell align='right' className={classes.cellSummaryLeft}>
                  <NumberFormat value={realOriginalPrice - invoice.totalDiscount} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right' className={classes.cellSummaryRight}>
                  Potongan
                </TableCell>
                <TableCell align='right' className={classes.cellSummaryLeft}>
                  <NumberFormat value={invoice.totalPiece} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                </TableCell>
              </TableRow>

              {invoicePayment &&
                invoicePayment.filter(payment => !payment.isDeleted).length > 0 &&
                invoicePayment.filter(payment => !payment.isDeleted).map((payment, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell
                        align='right'
                        className={classes.cellSummaryRightLink}
                        onClick={() => window.open(`/pembayaran-penjualan/${payment.id}`)}
                      >
                        {payment.paymentNumber}
                      </TableCell>
                      <TableCell align='right' className={classes.cellSummaryLeft}>
                        <NumberFormat
                          value={payment.InvoicePaymentMultiple.filter(value => value.InvoiceId === invoice.id)[0].totalPay}
                          prefix={'(Rp'}
                          suffix={')'}
                          thousandSeparator={true}
                          displayType='text'
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}

              <TableRow>
                <TableCell align='right' className={classes.cellSummaryRight}>
                  Total Tagihan
                </TableCell>
                <TableCell align='right' className={classes.cellSummaryLeft}>
                  {openTotalPrice && (
                    <>
                      <TextField
                        label='Total Harga'
                        value={totalPrice - invoice.totalPay}
                        onChange={e => setTotalPrice(+e.target.value + invoice.totalPay)}
                        InputProps={{
                          inputComponent: NumberFormatMask as any
                        }}
                        style={{ minWidth: 150 }}
                      />
                      &nbsp;
                      {loadingTotalPrice ? (
                        <CircularProgress size={20} color='inherit' />
                      ) : (
                        <>
                          <IconButton size='small' onClick={updateTotalPrice}>
                            <CheckIcon fontSize='small' />
                          </IconButton>
                          <IconButton size='small' onClick={handleCancelUpdateTotalPrice}>
                            <CancelIcon fontSize='small' color='action' />
                          </IconButton>
                        </>
                      )}
                    </>
                  )}

                  {!openTotalPrice && (
                    <>
                      <NumberFormat value={totalPrice - invoice.totalPay} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                      {invoice.statusPayment !== 'PAID' && (
                        <IconButton disabled={isCancel || !isAdmin || !isSuperAdmin} size='small' onClick={handleOpenUpdateTotalPrice}>
                          <EditIcon fontSize='small' />
                        </IconButton>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <ModalPermission setOpen={setOpen} open={open} />
    </Grid>
  );
};

export default ContentInvoice;
